import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { CostChart } from '@/components/cost-chart'
import { useDateWindow } from '@/components/date-range-picker'
import { DeltaPill } from '@/components/delta'
import { EmptyState } from '@/components/empty-state'
import { Panel } from '@/components/panel'
import { Skeleton } from '@/components/ui/skeleton'
import {
  COST_DIMENSIONS,
  type CostDimension,
  type CostFilters,
  costBands,
  DIMENSION_LABELS,
  isUnattributed,
  rowLabel,
  useCosts,
} from '@/lib/admin'
import { formatCount, relativeDelta } from '@/lib/format'
import { formatMoney, formatMoneyPrecise } from '@/lib/money'
import { DEFAULT_DATE_RANGE } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { ClearFilters, FilterSelect, Segmented } from './-parts'

interface AdminSearch extends CostFilters {
  groupBy?: CostDimension
}

function parseDimension(value: unknown): CostDimension | undefined {
  return typeof value === 'string' && (COST_DIMENSIONS as readonly string[]).includes(value)
    ? (value as CostDimension)
    : undefined
}

function parseText(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/** Which way the chart is cut: one setting with three values, not three
 * destinations. */
const CUTS = COST_DIMENSIONS.map((dimension) => ({
  value: dimension,
  label: DIMENSION_LABELS[dimension],
}))

export const Route = createFileRoute('/_app/admin/')({
  // The cut and the filters belong in the URL for the same reason the window
  // does everywhere else: a link to a finding has to mean the same thing for
  // whoever opens it.
  validateSearch: (search: Record<string, unknown>): AdminSearch => {
    const next: AdminSearch = {}
    const groupBy = parseDimension(search.groupBy)
    if (groupBy) next.groupBy = groupBy
    const provider = parseText(search.provider)
    if (provider) next.provider = provider
    const organizationId = parseText(search.organizationId)
    if (organizationId) next.organizationId = organizationId
    const siteId = parseText(search.siteId)
    if (siteId) next.siteId = siteId
    return next
  },
  // The staff gate lives on the parent layout, so every tab inherits it.
  component: AdminPage,
})

/**
 * What the deployment costs to run.
 *
 * The only page in the app that reads across tenants, and the only one that
 * names its vendors — everywhere else a provider is an implementation detail
 * the reports are careful never to mention, but here the vendor *is* the
 * subject.
 *
 * Cutting and filtering are separate controls on purpose. "Which provider is
 * this workspace's bill made of" is a routine question, and it needs a group-by
 * that stays independent of what has been narrowed to.
 */
function AdminPage() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const {
    range = DEFAULT_DATE_RANGE,
    groupBy = 'provider',
    provider,
    organizationId,
    siteId,
  } = search
  const [highlight, setHighlight] = useState<string | null>(null)

  const filters: CostFilters = { provider, organizationId, siteId }
  const { data, isPending } = useCosts(range, groupBy, filters)
  useDateWindow(data?.range)

  const breakdown = data?.breakdown ?? []
  const bands = costBands(groupBy, breakdown)
  const spent = data?.total.cost ?? 0
  const change = relativeDelta(spent, data?.total.previous ?? null)
  const nothingRecorded = !isPending && data?.earliest === null
  const filtered = Boolean(provider || organizationId || siteId)

  const set = (next: Partial<AdminSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }), replace: true })

  return (
    <>
      <h1 className="mono-label mb-3 text-muted-foreground">Costs</h1>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Segmented value={groupBy} options={CUTS} onChange={(next) => set({ groupBy: next })} />

        <span className="mx-1 h-4 w-px bg-border" aria-hidden />

        <FilterSelect
          all="All providers"
          options={data?.facets.providers ?? []}
          value={provider}
          format={(option) => rowLabel('provider', option)}
          onChange={(next) => set({ provider: next })}
        />
        <FilterSelect
          all="All workspaces"
          options={data?.facets.organizations ?? []}
          value={organizationId}
          format={(option) => rowLabel('organization', option)}
          onChange={(next) => set({ organizationId: next })}
        />
        <FilterSelect
          all="All sites"
          options={data?.facets.sites ?? []}
          value={siteId}
          format={(option) => rowLabel('site', option)}
          onChange={(next) => set({ siteId: next })}
        />

        {filtered && (
          <ClearFilters
            onClear={() =>
              set({ provider: undefined, organizationId: undefined, siteId: undefined })
            }
          />
        )}
      </div>

      {nothingRecorded ? (
        <Panel title="Spend">
          <EmptyState
            title="Nothing recorded yet"
            description="Every billed call lands here once a feed runs. Until then there is nothing to chart, which is not the same as a month that cost nothing."
          />
        </Panel>
      ) : (
        <div className="space-y-4">
          <Panel title="Spend" meta={data ? `${data.bucketDays}d buckets` : undefined} flush>
            <div className="border-b border-border px-5 py-4">
              <p className="mono-label text-muted-foreground">
                {filtered ? 'Total, filtered' : 'Total'}
              </p>
              <div className="flex items-baseline gap-2 pt-1">
                <span className="font-mono text-[28px] leading-none tabular">
                  {isPending ? <Skeleton className="inline-block h-7 w-24" /> : formatMoney(spent)}
                </span>
                {/* Spend going up is not good news, so the arrow's tint is
                    inverted against every other delta in the app. */}
                {data && <DeltaPill value={change} higherIsBetter={false} />}
              </div>
            </div>

            <div className="p-5">
              {isPending ? (
                <Skeleton className="h-[240px] w-full" />
              ) : (
                <CostChart
                  buckets={data?.buckets ?? []}
                  bands={bands}
                  bucketDays={data?.bucketDays ?? 1}
                  highlight={highlight}
                  onHighlight={setHighlight}
                />
              )}
            </div>
          </Panel>

          <Panel
            title={`By ${DIMENSION_LABELS[groupBy].toLowerCase()}`}
            meta={`${breakdown.length}`}
            flush
          >
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="mono-label px-5 py-2 text-left text-muted-foreground">
                    {DIMENSION_LABELS[groupBy]}
                  </th>
                  <th className="mono-label px-5 py-2 text-right text-muted-foreground">Calls</th>
                  <th className="mono-label px-5 py-2 text-right text-muted-foreground">Billed</th>
                  <th className="mono-label px-5 py-2 text-right text-muted-foreground">Failed</th>
                  <th className="mono-label px-5 py-2 text-right text-muted-foreground">Cost</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((row) => {
                  const band = bands.find((entry) => entry.key === row.key)
                  return (
                    <tr
                      key={row.key}
                      onPointerEnter={() => setHighlight(row.key)}
                      onPointerLeave={() => setHighlight(null)}
                      className={cn(
                        'border-b border-border transition-opacity last:border-0',
                        highlight !== null && highlight !== row.key && 'opacity-45',
                      )}
                    >
                      <td className="px-5 py-2">
                        <span className="flex items-center gap-2">
                          <span
                            className="size-2 shrink-0 rounded-[2px]"
                            style={{ background: band?.color }}
                            aria-hidden
                          />
                          <span
                            className={cn(
                              isUnattributed(row.key) && 'text-muted-foreground italic',
                            )}
                          >
                            {band?.label ?? row.label}
                          </span>
                        </span>
                      </td>
                      <td className="px-5 py-2 text-right font-mono text-[12px] tabular">
                        {formatCount(row.events)}
                      </td>
                      {/* The unit differs per provider — rows, credits, tokens
                          — so the count is shown without one rather than under a
                          heading that would be wrong for two of the three. */}
                      <td className="px-5 py-2 text-right font-mono text-[12px] text-muted-foreground tabular">
                        {formatCount(row.quantity)}
                      </td>
                      <td
                        className={cn(
                          'px-5 py-2 text-right font-mono text-[12px] tabular',
                          row.failed > 0 ? 'text-warning' : 'text-muted-foreground',
                        )}
                      >
                        {row.failed > 0 ? formatCount(row.failed) : '—'}
                      </td>
                      <td className="px-5 py-2 text-right font-mono text-[12px] tabular">
                        {formatMoneyPrecise(row.cost)}
                      </td>
                    </tr>
                  )
                })}

                {!isPending && breakdown.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-6 text-center text-muted-foreground">
                      Nothing was billed in this window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Panel>
        </div>
      )}
    </>
  )
}
